import Api from '../api/Api';
import Utils from '../utils/utils';
import Analytics from '../utils/analytics';

import Model from '../models/Model';
import Home from '../models/Home';

export const SET_HOMES = 'SET_HOMES';
export const ADD_HOME = 'ADD_HOME';
export const UPDATE_HOME = 'UPDATE_HOME';
export const SAVE_HOME = 'SAVE_HOME';
export const REMOVE_HOME = 'REMOVE_HOME';
export const REFRESH_HOMES = 'REFRESH_HOMES';

const trackCompletionForBatch = (previousHomes, nextHomes) => {
    (previousHomes ?? []).forEach((previousHome) => {
        const receivedHome = (nextHomes ?? []).find((h) => h?.id === previousHome?.id);
        Analytics.trackHomeCompletion(previousHome, receivedHome);
    });
};

export function setHomes(homes) {
    return (dispatch, getState) => {
        const previousHomes = getState().homes;
        const payload = homes.map((home) => new Home(home));
        dispatch({ type: SET_HOMES, payload });
        trackCompletionForBatch(previousHomes, payload);
    };
}

export function refreshHomes() {
    return {
        type: REFRESH_HOMES,
        payload: []
    };
}

export function addHome(home) {
    return {
        type: ADD_HOME,
        payload: new Home(home)
    };
}

export function setHome(home) {
    return (dispatch, getState) => {
        const payload = new Home(home);
        const currentHome = (getState().homes ?? []).find((h) => h?.id == payload?.id);
        dispatch({ type: UPDATE_HOME, payload });
        Analytics.trackHomeCompletion(currentHome, payload);
    };
}

const mergeHomesWithStatusFlags = (homes, statusHomeFlag) => {
    if (!homes || !statusHomeFlag) return homes;

    const flagsMap = {};
    statusHomeFlag.forEach((flag) => {
        const homeId = parseInt(flag.home_id, 10);
        flagsMap[homeId] = flag;
    });

    return homes.map((home) => {
        const homeInstance = home instanceof Home ? home : new Home(home);
        const homeFlag = flagsMap[homeInstance.id];

        if (homeFlag) {
            const homeData = homeInstance.toJSON();
            homeData.statusHomeFlag = homeFlag;
            return new Home(homeData);
        }

        return homeInstance;
    });
};

export function setHomesWithFlags(homes, statusHomeFlag) {
    return (dispatch, getState) => {
        const previousHomes = getState().homes;
        const mergedHomes = mergeHomesWithStatusFlags(homes, statusHomeFlag);
        dispatch({ type: SET_HOMES, payload: mergedHomes });
        trackCompletionForBatch(previousHomes, mergedHomes);
    };
}

export function fetchHomes() {
    return async (dispatch) => {
        try {
            const [homes, statusHomeFlagResponse] = await Promise.all([
                Api.Home.me(dispatch),
                Api.Home.getStatusHomeFlag()
            ]);
            const statusHomeFlag = statusHomeFlagResponse?.statusHomeFlag || statusHomeFlagResponse;

            if (statusHomeFlag && Array.isArray(statusHomeFlag)) {
                dispatch(setHomesWithFlags(homes, statusHomeFlag));
            } else {
                dispatch(setHomes(homes));
            }

            return { homes, statusHomeFlag };
        } catch (error) {
            console.error('Error fetching homes with flags:', error);
        }
    };
}

export function createHome(home) {
    let data = home instanceof Model ? home.toJSON() : home;
    data = Utils.featureArrayToFlag(data);
    return (dispatch) =>
        Api.Home.post(data).then((result) => {
            const homeResult = new Home(result);
            dispatch(addHome(homeResult));
            return homeResult;
        });
}

export function updateHome(home, refreshGP, additionalData = {}) {
    let data = home instanceof Model ? home.toJSON() : home;
    data = { ...data, ...additionalData };
    return (dispatch, getState) => {
        // TODO: API has trouble handle this property, return the right type (array)
        if (_.has(data, 'feature') && _.has(data.feature, 'others')) {
            delete data.feature.others;
        }
        data = Utils.featureArrayToFlag(data);

        // API will throw an error if sending "localized_size"
        if (_.has(data, 'detail') && _.has(data.detail, 'localized_size')) {
            delete data.detail.localized_size;
        }

        return Api.Home.patch(home.id, data, null, refreshGP, dispatch).then((result) => {
            const homeResult = new Home(result);
            const currentHome = (getState().homes ?? []).find((h) => h?.id == homeResult?.id);
            dispatch({
                type: SAVE_HOME,
                payload: homeResult
            });
            Analytics.trackHomeCompletion(currentHome, homeResult);
            return homeResult;
        });
    };
}

export function removeHome(home) {
    const data = home instanceof Model ? home.toJSON() : home;
    return (dispatch) => {
        return Api.Home.delete(home.id, dispatch).then(() => {
            dispatch({
                type: REMOVE_HOME,
                payload: new Home(data)
            });
        });
    };
}
